/**
 * ImagePromptEditor Component
 *
 * Displays and allows editing of image generation prompts with dual modes.
 * View mode shows prompt with two edit buttons: "Edit Manually" and "Edit with AI".
 * Manual mode shows textarea for direct editing.
 * AI mode shows AIImagePromptEditor for instruction-based editing.
 *
 * React 19 patterns:
 * - useReducer for complex state (view/manual/ai modes)
 * - useCallback for event handlers
 * - memo for performance
 * - Single return statement
 */

import { useReducer, useCallback } from 'react';
import { Edit3, Sparkles, X, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { cn } from '@/lib/utils';
import type { IImagePromptEditorProps } from '@/@types/images';
import { AIImagePromptEditorMemo } from './AIImagePromptEditor';

// ============================================
// STATE TYPES
// ============================================

interface EditorState {
  editMode: 'view' | 'manual' | 'ai';
  editedText: string;
}

type EditorAction =
  | { type: 'START_MANUAL_EDIT'; payload: string }
  | { type: 'START_AI_EDIT' }
  | { type: 'UPDATE_TEXT'; payload: string }
  | { type: 'CANCEL_EDIT' }
  | { type: 'SAVE_EDIT' };

// ============================================
// REDUCER
// ============================================

const initialState: EditorState = {
  editMode: 'view',
  editedText: '',
};

function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'START_MANUAL_EDIT':
      return {
        editMode: 'manual',
        editedText: action.payload,
      };
    case 'START_AI_EDIT':
      return {
        editMode: 'ai',
        editedText: '',
      };
    case 'UPDATE_TEXT':
      return {
        ...state,
        editedText: action.payload,
      };
    case 'CANCEL_EDIT':
      return {
        editMode: 'view',
        editedText: '',
      };
    case 'SAVE_EDIT':
      return {
        editMode: 'view',
        editedText: '',
      };
    default:
      return state;
  }
}

// ============================================
// COMPONENT
// ============================================

const ImagePromptEditor = ({
  promptId,
  postId,
  promptText,
  onRegenerateWithPrompt,
  isGenerating,
  className,
}: IImagePromptEditorProps) => {
  // ============================================
  // STATE
  // ============================================
  const [state, dispatch] = useReducer(editorReducer, initialState);

  // ============================================
  // EVENT HANDLERS
  // ============================================
  const handleManualEditClick = useCallback(() => {
    dispatch({ type: 'START_MANUAL_EDIT', payload: promptText || '' });
  }, [promptText]);

  const handleAIEditClick = useCallback(() => {
    dispatch({ type: 'START_AI_EDIT' });
  }, []);

  const handleCancelEdit = useCallback(() => {
    dispatch({ type: 'CANCEL_EDIT' });
  }, []);

  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    dispatch({ type: 'UPDATE_TEXT', payload: e.target.value });
  }, []);

  const handleRegenerate = useCallback(async () => {
    const textToUse = state.editMode === 'manual' ? state.editedText : promptText;
    if (!textToUse?.trim()) return;

    await onRegenerateWithPrompt(textToUse.trim());
    dispatch({ type: 'SAVE_EDIT' });
  }, [state.editMode, state.editedText, promptText, onRegenerateWithPrompt]);

  const handleAISaveSuccess = useCallback(
    async (editedPrompt: string) => {
      // Regenerate with the AI-edited prompt
      await onRegenerateWithPrompt(editedPrompt);
      dispatch({ type: 'SAVE_EDIT' });
    },
    [onRegenerateWithPrompt]
  );

  // ============================================
  // COMPUTED VALUES
  // ============================================
  const isViewMode = state.editMode === 'view';
  const isManualMode = state.editMode === 'manual';
  const isAIMode = state.editMode === 'ai';
  const hasPrompt = !!promptText;
  const hasChanges = isManualMode && state.editedText.trim() !== (promptText || '').trim();
  const canRegenerate = isManualMode ? !!state.editedText.trim() : hasPrompt;

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className={cn('rounded-lg border border-border bg-muted/50 p-4', className)}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium">Image Prompt</h4>
        {isManualMode && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancelEdit}
            className="h-7 px-2"
            disabled={isGenerating}
          >
            <X className="h-3.5 w-3.5 mr-1" />
            Cancel
          </Button>
        )}
      </div>

      {/* View Mode - Display prompt and edit buttons */}
      {isViewMode && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {promptText || 'No prompt generated yet.'}
          </p>
          {hasPrompt && !isGenerating && (
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={handleManualEditClick} className="h-8">
                <Edit3 className="h-3.5 w-3.5 mr-1.5" />
                Edit Manually
              </Button>
              <Button variant="default" size="sm" onClick={handleAIEditClick} className="h-8">
                <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                Edit with AI
              </Button>
            </div>
          )}
          {hasPrompt && !isGenerating && (
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRegenerate}
                disabled={isGenerating}
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                Regenerate image
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Manual Edit Mode - Textarea for direct editing */}
      {isManualMode && (
        <div className="space-y-3">
          <Textarea
            value={state.editedText}
            onChange={handleTextChange}
            placeholder="Enter image prompt..."
            className="min-h-[100px] text-sm"
            disabled={isGenerating}
          />
          <Button
            variant="default"
            className="w-full"
            onClick={handleRegenerate}
            disabled={!canRegenerate || isGenerating}
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                {hasChanges ? 'Regenerate with changes' : 'Regenerate image'}
              </>
            )}
          </Button>
        </div>
      )}

      {/* AI Edit Mode - AI-assisted editing interface */}
      {isAIMode && (
        <AIImagePromptEditorMemo
          promptId={promptId}
          postId={postId}
          originalPrompt={promptText || ''}
          onSaveSuccess={handleAISaveSuccess}
          onCancel={handleCancelEdit}
        />
      )}
    </div>
  );
};

export { ImagePromptEditor };
